import React, { useMemo, useState } from 'react';
import { Table, Spin, Empty, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PageLayout from '@components/layout/PageLayout';
import { useAuditSummaryData } from '@hooks/graphql/useAuditSummaryData';
import styles from './AuditSummary.module.css';
import { toTestAttrValue, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

interface AuditSummaryRow {
  category: string;
  occurrences: number;
}

const WINDOW_OPTIONS = [
  { value: 12, unit: 'hour', label: 'Last 12 Hours' },
  { value: 24, unit: 'hour', label: 'Last 24 Hours' },
  { value: 3, unit: 'day', label: 'Last 3 Days' },
  { value: 5, unit: 'day', label: 'Last 5 Days' },
  { value: 7, unit: 'day', label: 'Last 7 Days' },
  { value: 30, unit: 'day', label: 'Last 30 Days' },
] as const;

const columns: ColumnsType<AuditSummaryRow> = [
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'category',
    width: '50%',
    align: 'center',
    sorter: (a, b) => a.category.localeCompare(b.category),
  },
  {
    title: 'Occurrences',
    dataIndex: 'occurrences',
    key: 'occurrences',
    width: '50%',
    align: 'center',
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.occurrences - b.occurrences,
  },
];

const AuditSummary = () => {
  const [windowKey, setWindowKey] = useState<number>(0);
  const selectedWindow = WINDOW_OPTIONS[windowKey];

  const startDate = useMemo(
    () => dayjs().subtract(selectedWindow.value, selectedWindow.unit).toISOString(),
    [selectedWindow],
  );

  const { data, loading } = useAuditSummaryData(startDate);

  return (
    <PageLayout
      title="Audit Summary"
      headerAccessoryView={(
        <Select
          size="small"
          value={windowKey}
          onChange={setWindowKey}
          options={WINDOW_OPTIONS.map((opt, idx) => ({
            value: idx,
            label: opt.label,
            'data-testid': 'time-window-option',
            'data-value': toTestAttrValue(opt.label),
          }))}
          style={{ width: 150 }}
          data-testid="time-window-select"
          data-value={toTestAttrValue(selectedWindow.label)}
        />
      )}
    >
      <div className={styles.tableWrapper}>
        <Spin spinning={loading} data-testid="audit-summary-spinner" data-loading={String(loading)}>
          <Table
            columns={withTestColumnAttrs(columns, 'audit-summary-value')}
            dataSource={data}
            rowKey="category"
            pagination={false}
            size="middle"
            data-testid="audit-summary-table"
            data-row-count={String(data?.length ?? 0)}
            onRow={(record) =>
              ({
                'data-testid': 'audit-summary-row',
                'data-category': toTestAttrValue(record.category),
                'data-occurrences': String(record.occurrences),
              }) as TestAttrs
            }
            locale={{
              emptyText: (
                <Empty
                  description={`No audit activity in the ${selectedWindow.label.toLowerCase()}`}
                  data-testid="audit-summary-empty-state"
                />
              ),
            }}
          />
        </Spin>
      </div>
    </PageLayout>
  );
};

export default AuditSummary;
